/**
 * RegressionIndex — per-data-series row index that owns the
 * kernel-smoothed regression computation.
 *
 * Reads sorted `[x, y]` points from the parent data row's
 * `DataIndex.Series` and produces a regression curve + range-query
 * accessors. Lives outside the rendering layer so headless clients
 * + the SDK can query regression output without going through a DOM
 * binder. Ported from `tento/tento-lona-js/src/indices/regression-index.ts`
 * (Phase 3 / 3h).
 *
 * ## Sheet-level wiring
 *
 * Per §6.2 the per-sheet derived-store refactor lands after this
 * port; for now `RegressionIndex` exposes an explicit `populate`
 * entry point and an optional `subscribe(sheet, rowId, parent)`
 * helper that subscribes to `ColumnDataChanged` / `RowDataChanged
 * (rowId, "lookupKey")` on the sheet's invalidation sink. The
 * subscribe path mirrors `DataFacade` — swap it out for the
 * sheet-level store when §6.2 lands.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import {
  ColumnDataChanged,
  type Invalidation,
  type InvalidationSink,
  RowDataChanged,
} from "../tx/invalidation.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import {
  kernelSmooth,
  type KernelSmoothResult,
  type RegressionPoint,
} from "../regression/kernel-smooth.ts";
import type { DataIndex } from "./data-index.ts";
import type { PointY } from "../stores/graph-types.ts";
import { elementAt } from "../core/checked-index.ts";
import type { Unsubscribe } from "../backends/rows-backend.ts";

/**
 * Attributes a regression-type data-series row carries. Typically
 * sourced from `RowMetadata.attributes`; callers pass the already-
 * narrowed bag.
 */
export interface RegressionAttributes {
  readonly seriesKey?: string;
  readonly convHalfWidth?: number;
  readonly maxGap?: number;
  readonly minPoints?: number;
  readonly minCoverage?: number;
}

export namespace RegressionAttributes {
  /** Read the regression keys off a row's attribute bag (each optional). */
  export function decode(attributes: JsonObject): RegressionAttributes {
    const seriesKey = Json.string(attributes, "seriesKey");
    const convHalfWidth = Json.number(attributes, "convHalfWidth");
    const maxGap = Json.number(attributes, "maxGap");
    const minPoints = Json.number(attributes, "minPoints");
    const minCoverage = Json.number(attributes, "minCoverage");
    return {
      ...(seriesKey != null ? { seriesKey } : {}),
      ...(convHalfWidth != null ? { convHalfWidth } : {}),
      ...(maxGap != null ? { maxGap } : {}),
      ...(minPoints != null ? { minPoints } : {}),
      ...(minCoverage != null ? { minCoverage } : {}),
    };
  }
}

/**
 * Minimal parent-index contract — the only method `populate`
 * reads off the parent. Widened from `DataIndex` so lona-js-side
 * shims can pass their own nominally-distinct `DataIndex`
 * without a cast.
 */
export interface SeriesProvider {
  peekSeries(key: string): Option<DataIndex.Series>;
}

/**
 * The index behind `RegressionFacade`: one series' points and their smoothed
 * curve.
 */
export class RegressionIndex {
  #pointList: readonly [number, number][] = [];
  #regressionResult: Option<KernelSmoothResult> = null;
  #currentSeriesKey: Option<string> = null;

  // ── Populate ────────────────────────────────────────────────────

  /**
   * Rebuild the index from a parent `DataIndex.Series`. Pass `null`
   * for `series` when the parent has no matching series key — the
   * index clears to an empty state.
   */
  populate(
    attrs: Option<RegressionAttributes>,
    parent: Option<SeriesProvider>,
  ): void {
    if (!attrs) {
      this.#clear();
      return;
    }
    const seriesKey = attrs.seriesKey ?? "default";
    this.#currentSeriesKey = seriesKey;

    const series = parent?.peekSeries(seriesKey) ?? null;
    if (!series || series.points.length < 2) {
      this.#pointList = numericPoints(series?.points ?? []);
      this.#regressionResult = null;
      return;
    }

    // Regression only makes sense on numeric y: skip non-numeric points
    // (an OHLC or record series) rather than producing NaN curves.
    const points = numericPoints(series.points);
    this.#pointList = points;

    if (points.length < 2) {
      this.#regressionResult = null;
      return;
    }

    const halfWidth = attrs.convHalfWidth ?? series.convHalfWidth ?? 10;
    const maxGap = attrs.maxGap ?? series.maxGap ?? halfWidth;
    const minPoints = attrs.minPoints ?? series.minPoints ?? 3;
    const minCoverage = attrs.minCoverage ?? series.minCoverage ?? 0.5;

    // Split into contiguous segments at gaps > maxGap. Each segment
    // gets its own regression — no curve spans a large gap.
    const segments = segmentByGap(points, maxGap);
    const tailLimit = halfWidth / 4;
    const cadence = computeCadence(segments, maxGap);
    const curves: RegressionPoint[] = [];
    let xMin = Infinity;
    let xMax = -Infinity;
    for (const seg of segments) {
      if (seg.length < minPoints) continue;
      if (!hasMinCoverage(seg, cadence, minCoverage)) continue;

      const result = kernelSmooth(seg, halfWidth, null);
      const segMin = elementAt(seg, 0, "regression-index: seg")[0];
      const segMax = elementAt(seg, seg.length - 1, "regression-index: seg")[0];
      for (const p of result.curve) {
        if (p.x >= segMin - tailLimit && p.x <= segMax + tailLimit) {
          curves.push(p);
        }
      }
      xMin = Math.min(xMin, result.xMin);
      xMax = Math.max(xMax, result.xMax);
    }
    this.#regressionResult = curves.length > 0
      ? { curve: curves, xMin, xMax }
      : null;
  }

  // ── Queries ─────────────────────────────────────────────────────

  get seriesKey(): Option<string> {
    return this.#currentSeriesKey;
  }

  get points(): readonly [number, number][] {
    return this.#pointList;
  }

  get regression(): Option<KernelSmoothResult> {
    return this.#regressionResult;
  }

  /** Raw points within an x-range `[start, end)`. */
  peekPointsInRange(start: number, end: number): [number, number][] {
    if (this.#pointList.length === 0) return [];

    let lo = 0;
    let hi = this.#pointList.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (elementAt(this.#pointList, mid, "regression-index: this.pointList")[0] < start) lo = mid + 1;
      else hi = mid;
    }

    const result: [number, number][] = [];
    for (let i = lo; i < this.#pointList.length; i++) {
      const point = elementAt(this.#pointList, i, "RegressionIndex.peekPointsInRange: pointList");
      if (point[0] >= end) break;
      result.push(point as [number, number]);
    }
    return result;
  }

  /** Regression-curve points within an x-range `[start, end]`. */
  peekRegressionInRange(start: number, end: number): RegressionPoint[] {
    if (!this.#regressionResult) return [];
    return this.#regressionResult.curve.filter(
      (p) => p.x >= start && p.x <= end,
    );
  }

  // ── Internal ────────────────────────────────────────────────────

  #clear(): void {
    this.#pointList = [];
    this.#regressionResult = null;
  }
}

// ── Sheet-level subscription helper ─────────────────────────────────

export namespace RegressionIndex {
  /**
   * Attach a `RegressionIndex` to a row by wiring it to the sheet's
   * invalidation sink. Rebuilds the index when:
   *
   *   - `ColumnDataChanged(parentId)` fires — parent cells changed.
   *   - `RowDataChanged(rowId, "lookupKey" | "attributes")` fires —
   *     this row's series configuration changed.
   *   - `RowDataChanged(parentId, "lookupKey")` fires — parent's
   *     data-preset switched.
   *
   * `resolveAttrs` + `resolveParent` are injection points so this
   * helper doesn't reach directly into `Sheet` (keeps the port free
   * of cycles). The returned `dispose` unsubscribes.
   */
  export interface SubscribeOptions {
    readonly sink: InvalidationSink;
    readonly rowId: RowLocalId;
    readonly parentId: RowLocalId;
    resolveAttrs(): Option<RegressionAttributes>;
    resolveParent(): Option<SeriesProvider>;
  }

  export function subscribe(
    index: RegressionIndex,
    opts: SubscribeOptions,
  ): Unsubscribe {
    const rebuild = () => index.populate(opts.resolveAttrs(), opts.resolveParent());
    // Initial populate so callers can query immediately.
    rebuild();
    return opts.sink.subscribe((e: Invalidation) => {
      if (e instanceof ColumnDataChanged && e.rowId === opts.parentId) {
        rebuild();
      } else if (
        e instanceof RowDataChanged &&
        (e.rowId === opts.rowId || e.rowId === opts.parentId)
      ) {
        rebuild();
      }
    });
  }
}

// ── Segmentation helpers ──────────────────────────────────────────

/**
 * Compute the expected cadence (spacing between consecutive points)
 * for the series by taking the median of within-segment gaps. Cross-
 * segment gaps are excluded because they're by definition larger
 * than `maxGap` and don't represent the series's natural spacing.
 */
function computeCadence(
  segments: readonly [number, number][][],
  fallback: number,
): number {
  const gaps: number[] = [];
  for (const seg of segments) {
    for (let i = 1; i < seg.length; i++) {
      gaps.push(elementAt(seg, i, "regression-index: seg")[0] - elementAt(seg, i - 1, "regression-index: seg")[0]);
    }
  }
  if (gaps.length === 0) return fallback;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)] || fallback;
}

/**
 * Coverage = points / expected-positions-at-cadence. Cadence is the
 * series's expected spacing (computed from all within-segment gaps).
 *
 * Examples (daily cadence = 1):
 *   3 points at x = [0, 1, 2]   → span=2,  expected=3,  coverage=1.00 → dense
 *   3 points at x = [0, 3, 6]   → span=6,  expected=7,  coverage=0.43 → sparse
 *   23 points at x = [0..24]    → span=24, expected=25, coverage=0.92 → dense
 */
function hasMinCoverage(
  points: readonly [number, number][],
  cadence: number,
  minCoverage: number,
): boolean {
  if (points.length < 2) return false;
  const span = elementAt(points, points.length - 1, "regression-index: points")[0] - elementAt(points, 0, "regression-index: points")[0];
  if (span <= 0 || cadence <= 0) return true;

  const expectedPositions = Math.floor(span / cadence) + 1;
  const coverage = points.length / expectedPositions;
  return coverage >= minCoverage;
}

/** Split sorted points into contiguous segments at gaps > maxGap. */
function segmentByGap(
  points: readonly [number, number][],
  maxGap: number,
): [number, number][][] {
  const segments: [number, number][][] = [];
  let current: [number, number][] = [];
  for (const p of points) {
    const last = current[current.length - 1];
    if (last !== undefined && p[0] - last[0] > maxGap) {
      segments.push(current);
      current = [];
    }
    current.push(p);
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

export type { KernelSmoothResult, RegressionPoint };

/** The points whose `Y` is a finite number (a regression's input). */
function numericPoints(points: readonly [number, PointY][]): [number, number][] {
  const out: [number, number][] = [];
  for (const [x, y] of points) {
    if (typeof y === "number" && Number.isFinite(y)) out.push([x, y]);
  }
  return out;
}
